import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Star, StarHalf, MessageSquare, ThumbsUp, Eye, EyeOff, Search, Filter,
  TrendingUp, Users, Package, CheckCircle, AlertCircle, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const ReadMoreText = ({ text, maxLength = 150 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!text || text.length <= maxLength) {
    return <p className="text-gray-700 mt-1 text-sm md:text-base">{text}</p>;
  }
  
  return (
    <div>
      <p className="text-gray-700 mt-1 text-sm md:text-base">
        {isExpanded ? text : `${text.substring(0, maxLength)}...`}
      </p>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-blue-600 text-sm hover:underline mt-1"
      >
        {isExpanded ? 'Read less' : 'Read more'}
      </button>
    </div>
  );
};

const RatingsReviews = () => {
  const { apiCall, isAdmin } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [totalReviews, setTotalReviews] = useState(0);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Response modal
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [statusFilter, ratingFilter]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      let url = '/api/reviews/all?limit=100';
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (ratingFilter !== 'all') {
        url += `&rating=${ratingFilter}`;
      }
      
      const data = await apiCall(url);
      setReviews(data.reviews || []);
      setTotalReviews(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      toast({
        title: 'Error',
        description: 'Failed to load reviews',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // For vendors, get their own stats
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      if (user.id) {
        const data = await apiCall(`/api/reviews/stats/vendor/${user.id}`);
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleResponse = async () => {
    if (!respondingTo || !responseText.trim()) return;
    
    setSubmitting(true);
    try {
      await apiCall(`/api/reviews/response/${respondingTo}?response_text=${encodeURIComponent(responseText)}`, {
        method: 'POST'
      });
      toast({
        title: 'Success',
        description: 'Response added successfully'
      });
      setRespondingTo(null);
      setResponseText('');
      fetchReviews();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add response',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePublish = async (reviewId, currentStatus) => {
    try {
      await apiCall(`/api/reviews/${reviewId}/publish?publish=${!currentStatus}`, {
        method: 'PUT'
      });
      toast({
        title: 'Success',
        description: `Review ${!currentStatus ? 'published' : 'unpublished'} successfully`
      });
      fetchReviews();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update review',
        variant: 'destructive'
      });
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />);
    }
    if (hasHalfStar) {
      stars.push(<StarHalf key="half" className="h-4 w-4 fill-yellow-400 text-yellow-400" />);
    }
    for (let i = stars.length; i < 5; i++) {
      stars.push(<Star key={i} className="h-4 w-4 text-gray-300" />);
    }
    return stars;
  };

  const filteredReviews = reviews.filter(review => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      review.customer_name?.toLowerCase().includes(query) ||
      review.text?.toLowerCase().includes(query) ||
      review.listing_title?.toLowerCase().includes(query)
    );
  });

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Ratings & Reviews</h1>
            <p className="text-gray-500 mt-1">Manage customer reviews and feedback</p>
          </div>
          <Button variant="outline" onClick={() => { fetchReviews(); fetchStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="flex justify-center mb-2">
                    {renderStars(stats.average_rating)}
                  </div>
                  <p className="text-2xl font-bold">{stats.average_rating}</p>
                  <p className="text-sm text-gray-500">Average Rating</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <MessageSquare className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                  <p className="text-2xl font-bold">{stats.total_reviews}</p>
                  <p className="text-sm text-gray-500">Total Reviews</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Star className="h-8 w-8 mx-auto text-yellow-500 mb-2" />
                  <p className="text-2xl font-bold">{stats.rating_distribution?.['5'] || 0}</p>
                  <p className="text-sm text-gray-500">5-Star Reviews</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <CheckCircle className="h-8 w-8 mx-auto text-green-500 mb-2" />
                  <p className="text-2xl font-bold">{stats.verified_purchases}</p>
                  <p className="text-sm text-gray-500">Verified Purchases</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Users className="h-8 w-8 mx-auto text-purple-500 mb-2" />
                  <p className="text-2xl font-bold">{stats.internal_reviews}</p>
                  <p className="text-sm text-gray-500">Internal Reviews</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <TrendingUp className="h-8 w-8 mx-auto text-orange-500 mb-2" />
                  <p className="text-2xl font-bold">{stats.external_reviews}</p>
                  <p className="text-sm text-gray-500">External Reviews</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Rating Distribution */}
        {stats && (
          <Card>
            <CardHeader>
              <CardTitle>Rating Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map(rating => {
                  const count = stats.rating_distribution?.[String(rating)] || 0;
                  const percentage = stats.total_reviews > 0 
                    ? Math.round((count / stats.total_reviews) * 100) 
                    : 0;
                  return (
                    <div key={rating} className="flex items-center gap-3">
                      <span className="w-8 text-sm font-medium">{rating} ★</span>
                      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-yellow-400 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="w-16 text-sm text-gray-600 text-right">{count} ({percentage}%)</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search reviews..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="unpublished">Unpublished</SelectItem>
                </SelectContent>
              </Select>
              <Select value={ratingFilter} onValueChange={setRatingFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ratings</SelectItem>
                  <SelectItem value="5">5 Stars</SelectItem>
                  <SelectItem value="4">4 Stars</SelectItem>
                  <SelectItem value="3">3 Stars</SelectItem>
                  <SelectItem value="2">2 Stars</SelectItem>
                  <SelectItem value="1">1 Star</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Reviews List */}
        <Card>
          <CardHeader>
            <CardTitle>Reviews ({filteredReviews.length})</CardTitle>
            <CardDescription>Customer feedback for your listings</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="h-8 w-8 mx-auto animate-spin text-gray-400" />
                <p className="mt-2 text-gray-500">Loading reviews...</p>
              </div>
            ) : filteredReviews.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-600">No reviews yet</h3>
                <p className="text-gray-500 mt-1">Reviews from customers will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredReviews.map((review) => (
                  <div 
                    key={review.review_id} 
                    className={`border rounded-lg p-4 ${!review.is_published ? 'bg-gray-50 border-dashed' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        {/* Listing Image */}
                        {review.listing_image && (
                          <img 
                            src={review.listing_image} 
                            alt={review.listing_title}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                        )}
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <div className="flex">{renderStars(review.rating)}</div>
                            <span className="font-medium">{review.rating}</span>
                            {!review.is_published && (
                              <Badge variant="secondary">Unpublished</Badge>
                            )}
                            {review.is_verified_purchase && (
                              <Badge className="bg-green-100 text-green-800">Verified Purchase</Badge>
                            )}
                          </div>
                          {review.listing_title && (
                            <div className="text-sm text-gray-500 mb-1">
                              <Package className="h-3 w-3 inline mr-1" />
                              {review.listing_title}
                              {review.listing_type && (
                                <Badge variant="outline" className="ml-2 text-xs">{review.listing_type}</Badge>
                              )}
                            </div>
                          )}
                          {review.title && (
                            <h4 className="font-semibold">{review.title}</h4>
                          )}
                          <ReadMoreText text={review.text} />
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span>{review.customer_name || 'Anonymous'}</span>
                            <span>•</span>
                            <span>{new Date(review.submission_date).toLocaleDateString()}</span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <ThumbsUp className="h-3 w-3" />
                              {review.helpful_count} helpful
                            </span>
                            <span>•</span>
                            <span className="capitalize">{review.source}</span>
                          </div>
                          
                          {/* Response from Admin/Vendor */}
                          {review.vendor_response && (
                            <div className={`mt-3 p-3 rounded-lg border-l-4 ${review.response_author === 'Admin' ? 'bg-purple-50 border-purple-500' : 'bg-blue-50 border-blue-500'}`}>
                              <p className={`text-sm font-medium mb-1 ${review.response_author === 'Admin' ? 'text-purple-900' : 'text-blue-900'}`}>
                                {review.response_author === 'Admin' ? 'Admin Response' : 'Vendor Response'}
                              </p>
                              <p className={`text-sm ${review.response_author === 'Admin' ? 'text-purple-800' : 'text-blue-800'}`}>{review.vendor_response}</p>
                              <p className={`text-xs mt-1 ${review.response_author === 'Admin' ? 'text-purple-600' : 'text-blue-600'}`}>
                                {review.vendor_response_date && new Date(review.vendor_response_date).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                          
                          {/* Response Input */}
                          {respondingTo === review.review_id && (
                            <div className="mt-3 space-y-2">
                              <Textarea
                                placeholder="Write your response..."
                                value={responseText}
                                onChange={(e) => setResponseText(e.target.value)}
                                rows={3}
                              />
                              <div className="flex gap-2">
                                <Button size="sm" onClick={handleResponse} disabled={submitting}>
                                  {submitting ? 'Submitting...' : 'Submit Response'}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => {
                                  setRespondingTo(null);
                                  setResponseText('');
                                }}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        {!review.vendor_response && respondingTo !== review.review_id && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setRespondingTo(review.review_id)}
                          >
                            <MessageSquare className="h-4 w-4 mr-1" />
                            Respond
                          </Button>
                        )}
                        {isAdmin() && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleTogglePublish(review.review_id, review.is_published)}
                          >
                            {review.is_published ? (
                              <>
                                <EyeOff className="h-4 w-4 mr-1" />
                                Unpublish
                              </>
                            ) : (
                              <>
                                <Eye className="h-4 w-4 mr-1" />
                                Publish
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default RatingsReviews;
